import { useQueryClient } from '@tanstack/react-query'
import { BriefcaseBusiness, Pencil, Users } from 'lucide-react'
import { useContext, useState } from 'react'
import { toast } from 'sonner'

import SensitiveFieldRow from 'components/brokers/SensitiveFieldRow'
import EmployerContext from 'components/context/EmployerContext'
import { Badge, DropdownMenu } from 'slab'
import { getBusinessTypeDisplayText } from 'utils/employer_utils'
import { getGetAdminEmployerProfilesEmployerProfileIdQueryKey, usePatchAdminEmployerProfilesEmployerProfileId } from 'utils/web_api'

import { CollapsibleDataContainer } from '../reusable/CollapsibleDataContainer'
import { CopyFieldRow } from '../reusable/CopyFieldRow'
import TableSkeleton from '../reusable/table/Skeleton'

function formatBooleanValue (value: boolean | null | undefined): string {
  if (value === true) return 'Yes'
  if (value === false) return 'No'
  return '(none)'
}

interface EditableBooleanFieldRowProps {
  label: string
  value: boolean | null | undefined
  onUpdate: (value: boolean) => Promise<void>
  isUpdating: boolean
}

function EditableBooleanFieldRow ({ label, value, onUpdate, isUpdating }: EditableBooleanFieldRowProps) {
  const displayValue = formatBooleanValue(value)
  const isNone = value === null || value === undefined

  return (
    <div className='px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0'>
      <dt className='text-sm font-medium leading-6 text-gray-900'>{label}</dt>
      <dd className='mt-1 text-sm leading-6 sm:col-span-2 sm:mt-0 flex items-center'>
        <span className={isNone ? 'text-gray-400' : 'text-gray-700'}>{displayValue}</span>
        <DropdownMenu
          trigger={
            <button
              className='ml-2 p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50'
              disabled={isUpdating}
            >
              <Pencil size={14} />
            </button>
          }
        >
          <DropdownMenu.Item onSelect={() => onUpdate(true)}>Yes</DropdownMenu.Item>
          <DropdownMenu.Item onSelect={() => onUpdate(false)}>No</DropdownMenu.Item>
        </DropdownMenu>
      </dd>
    </div>
  )
}

export default function EmployerCorporateStructure () {
  const { profile } = useContext(EmployerContext)
  const queryClient = useQueryClient()
  const [isUpdating, setIsUpdating] = useState<string | null>(null)

  const patchProfile = usePatchAdminEmployerProfilesEmployerProfileId()

  const handleUpdate = async (field: 'is_publicly_traded' | 'is_nonprofit', value: boolean) => {
    if (!profile?.id) return

    setIsUpdating(field)
    try {
      await patchProfile.mutateAsync({
        employerProfileId: profile.id,
        data: { [field]: value },
      })
      await queryClient.invalidateQueries({ queryKey: getGetAdminEmployerProfilesEmployerProfileIdQueryKey(profile.id) })
      toast.success('Profile updated')
    } catch {
      toast.error('Failed to update profile')
    } finally {
      setIsUpdating(null)
    }
  }

  const heading = (
    <div className='flex items-center space-x-2'>
      <span className='heading-md'>Corporate structure</span>

      {/* Business Type Badge */}
      {profile?.business_type && (<Badge type='info' leftIcon={BriefcaseBusiness}>{getBusinessTypeDisplayText(profile?.business_type)}</Badge>)}

      {/* Number of Employees Badge */}
      {profile?.number_of_employees && (<Badge type='info' leftIcon={Users}>{`${profile?.number_of_employees} employees`}</Badge>)}
    </div>
  )

  const successContent = (
    <dl className='divide-y divide-gray-100 p-4'>
      <CopyFieldRow label='Business Type' value={getBusinessTypeDisplayText(profile?.business_type)} />
      <CopyFieldRow label='Number of Employees' value={profile?.number_of_employees?.toString()} />
      <SensitiveFieldRow
        enableCopyButton
        isHidden
        label='EIN'
        value={profile?.ein}
      />
      <EditableBooleanFieldRow
        label='Publicly Traded'
        value={profile?.is_publicly_traded}
        onUpdate={(value) => handleUpdate('is_publicly_traded', value)}
        isUpdating={isUpdating === 'is_publicly_traded'}
      />
      <EditableBooleanFieldRow
        label='Nonprofit'
        value={profile?.is_nonprofit}
        onUpdate={(value) => handleUpdate('is_nonprofit', value)}
        isUpdating={isUpdating === 'is_nonprofit'}
      />
    </dl>
  )

  return (
    <CollapsibleDataContainer
      heading={heading}
      isLoading={!profile}
      error={!profile}
      localStorageKey='employerInformationOpenByDefault'
      loadingContent={<TableSkeleton rowCount={3} columnCount={3} />}
      errorContent={
        <div className='p-4 text-center text-red-500'>
          Error loading employer information
        </div>
      }
      successContent={successContent}
    />
  )
}
